import { runtimeConfig } from "config/runtimeConfig";
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AppContext } from 'Context/AppContext';
import { CopyIcon, DeleteIcon, PreviewIcon } from '../../../../../../assests/GmsIcons';
import Table from 'Components/DesignSystem/Table/Table';
import { TABBED_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import { TableColumn } from '../../../../../../types/DesignSystem/Table';
import Search from 'Components/DesignSystem/Search/Search';
import Button from 'Components/DesignSystem/Button/Button';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import Status from 'Components/DesignSystem/Status/Status';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import Typography from 'Components/DesignSystem/Typography/Typography';
import CreateUIModel from '../PopUp/CreateUIModel';
import FreestyleBadge from '../Common/FreestyleBadge';
import { isFreestyle } from '../../selectors';
import { TPaginationParams } from '../../../../Performance/analytics';
import { useModule } from '../../contexts/ModuleContext';
import { fetchUIModelsApi, deleteUIModelApi, getUIModelById } from '../../services/ApiHelper';
import { UIModelAPI } from '../../types/api.types';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import DeleteProjectModal from '../../../Components/DeleteProjectModal';
import { formatDateWithTimeDDMMMYYYY } from '../../../../../../Common/HelperClass';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { TParty } from '../../../../../../types/Context/AppContext';

type UIModelTableProps = {
  id?: string;
  projectId: string;
  selectedParty: TParty;
  refreshRef?: React.MutableRefObject<(() => void) | null>;
  industryId?: string;
  onFreestyleUIModelChange?: (hasFreestyle: boolean) => void;
};

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 15, label: '15' },
  { value: 20, label: '20' },
  { value: 25, label: '25' },
];

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * The UI Model tab's models list, chrome on the design system (§ 101). The
 * fetch/sort/search wiring is untouched; what changed is the shell: DS Table
 * (keyboard-sortable headers with aria-sort, skeleton loading rows) replacing
 * the local DataTable/TableHeader/TableCell, DS Search replacing the popup
 * Input, per-row icon buttons templated from the model id (retiring
 * ActionButton's shared testid), DS Status for the promoted chip, and the
 * § 87 DS Dropdown + Pagination strip replacing the raw <select> +
 * PaginatorWithApi. The Create/Copy popups are DS Dialogs owned by
 * `CreateUIModel` (§ 102) — the local PopUp shell retired.
 */
const UIModelTable = ({
  id,
  projectId,
  selectedParty,
  refreshRef,
  industryId,
  onFreestyleUIModelChange,
}: UIModelTableProps) => {
  const baseId = id || 'ui-model-table-1';
  const appData = useContext<any>(AppContext);
  const [paginationParams, setPaginationParams] = useState<TPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const { dispatch } = useUIModelState();
  const [models, setModels] = useState<UIModelAPI[]>([]);
  const [showCreateUiModel, setShowCreateUiModel] = useState(false);
  const [copyUiModel, setCopyUiModel] = useState<UIModelAPI | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [deleteModal, setDeleteModal] = useState<UIModelAPI | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [sort, setSort] = useState<{ sortBy: string; sortOrder: string }>({
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { setActiveView, hasEditPermission } = useModule();

  const fetchModels = useCallback(async () => {
    setIsLoading(true);
    try {
      const responseData = await fetchUIModelsApi(
        projectId,
        paginationParams,
        sort.sortBy,
        sort.sortOrder,
        searchTerm
      );
      if (responseData?.done) {
        setTotalRecords(responseData.data.total ?? 10);
        const fetchedModels: UIModelAPI[] = responseData.data.models;
        setModels(fetchedModels);
        onFreestyleUIModelChange?.(fetchedModels.some((m) => m.composition_mode === 'freestyle'));
      }
    } catch (error) {
      console.error('Failed to fetch UI models:', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, paginationParams, sort, searchTerm]);

  // Expose fetchModels to parent via ref
  useEffect(() => {
    if (refreshRef) {
      refreshRef.current = fetchModels;
    }
    return () => {
      if (refreshRef) {
        refreshRef.current = null;
      }
    };
  }, [refreshRef, fetchModels]);

  useEffect(() => {
    fetchModels();
  }, [projectId, paginationParams, sort]);

  useEffect(() => {
    fetchModels();
  }, [debouncedSearchTerm]);

  const handleCreateSuccess = () => {
    setShowCreateUiModel(false);
    fetchModels();
  };

  const loadExistingUIModel = async (modelId: string): Promise<void> => {
    try {
      const response = await getUIModelById(projectId, modelId);
      if (response?.done && response.data?.model) {
        dispatch({ type: 'SET_MODEL', payload: response.data.model });
      } else {
        throw new Error('Failed to load model');
      }
    } catch (error) {
      console.error('Failed to load UI model:', error);
      throw error;
    }
  };

  const deleteUIModel = async (uiModelId: string): Promise<{ done: boolean; message?: string }> => {
    const result = await deleteUIModelApi(projectId, uiModelId);
    return result ?? { done: false, message: 'Failed to delete UI model' };
  };

  const handleModelClick = async (model: UIModelAPI) => {
    try {
      await loadExistingUIModel(model.id);
      setActiveView('editor');
    } catch (error) {
      console.error('Failed to load UI model:', error);
      toast.error('Failed to load UI model');
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      pageNumber: pageNumber,
    }));
  };

  const updatePageSize = (pageSize: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      pageSize: pageSize,
      pageNumber: 1,
    }));
  };

  const handleSort = async (column: string) => {
    if (sort.sortBy === column) {
      setSort({ sortBy: column, sortOrder: sort.sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ sortBy: column, sortOrder: 'asc' });
    }
  };

  const renderDeleteButton = (model: UIModelAPI, rowIndex: number) => {
    const button = (
      <button
        type="button"
        id={`delete-btn-${rowIndex}`}
        data-testid={`ui-model-delete-${model.id}`}
        aria-label={`Delete ${model?.name ?? ''}`}
        onClick={() => {
          if (!hasEditPermission()) {
            toast.error('You dont have permission to update this model');
            return;
          }
          setDeleteModal(model);
        }}
        disabled={model.is_promoted}
        className={model.is_promoted ? 'tw-opacity-30 tw-cursor-not-allowed' : undefined}
      >
        <DeleteIcon />
      </button>
    );
    // Tooltip's own wrapper span carries the hover listeners, so the reason
    // stays reachable over the disabled button (a title attr on a disabled
    // control is unreliable across browsers).
    return model.is_promoted ? (
      <Tooltip usePortal placement="bottom" content="Cannot delete a promoted UI model">
        {button}
      </Tooltip>
    ) : button;
  };

  /* One glyph for "this row has no value", so an absent field never reads as a cell
     that failed to load. `—` is the placeholder already used elsewhere in GMS
     (`BaseMappingTable`, Commend 3, Product Cards' `EMPTY_DATE_PLACEHOLDER`). */
  const EMPTY_CELL = '\u2014';

  const columns: TableColumn<UIModelAPI>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      /*
       * 440px, up from 320. **Not to fill the void — to stop truncating.** Measured on
       * `/gms/guide` → UI Model at a 1238px table:
       *
       * | piece | px |
       * |---|---|
       * | a real Guide-style name ("Copy of Optimization check 15/09/26 renamed") | 314 |
       * | the Promoted chip that sits beside it | 91 |
       * | the flex gap | 8 |
       * | **worst realistic case** | **413** |
       *
       * At 320 that overflowed by 93px, so a long name lost its tail while 246px of
       * spacer sat idle two columns away. 440 clears the worst case with headroom.
       *
       * **It deliberately does NOT take all the slack.** Real names measure 75–128px
       * here; a Name column swollen to the full 566 would put ~400px of dead space
       * between most names and their Updated date, which is harder to scan than one gap
       * parked at the end of the row. The spacer below keeps ~126px and the action
       * icons stay pinned right. The original decision to park the void in one place
       * was right; only the 320 was wrong.
       */
      width: '440px',
      sortable: true,
      render: (_value, model) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          {/* TODO(restore): promoted UI models were not editable (non-clickable name). Restriction temporarily lifted so the owner can edit. */}
          <div
            role="button"
            data-testid={`ui-model-name-${model.id}`}
            onClick={() => handleModelClick(model)}
            className="tw-cursor-pointer tw-text-accent-bold hover:tw-underline"
          >
            {model?.name ?? ''}
          </div>
          {model.is_promoted ? (
            <Tooltip
              usePortal
              placement="bottom"
              content={model.hasUnpromotedChanges
                ? 'This model has unpromoted changes. Promote to apply.'
                : 'This model is promoted (live)'}
            >
              <Status
                tone="success"
                label="Promoted"
                showAlert={model.hasUnpromotedChanges}
                alertLabel="Has unpromoted changes"
                data-testid={`ui-model-status-${model.id}`}
              />
            </Tooltip>
          ) : null}
          {isFreestyle(model) && <FreestyleBadge id={`ui-model-freestyle-${model.id}`} />}
        </div>
      ),
    },
    {
      /*
       * Replaces the short-lived "Updated by" column. A UI model belongs to one person
       * — whoever creates it is whoever edits it — so `updatedByUsername` printed the
       * SAME name on every row and carried no distinguishing information. Confirmed on
       * `/gms/guide` before removing it: three rows, one name. A column identical down
       * its whole length is a column not paying for its width.
       *
       * `createdAt` earns the slot instead, and fixes a quiet lie next door: the
       * Updated column reads `updatedAt ?? createdAt`, so a model never edited since
       * creation showed its CREATION date under a header saying "Updated". With both
       * columns present that fallback is legible rather than misleading.
       *
       * It sits BEFORE Updated so the row reads as a timeline — created, updated, last
       * promoted — which is also the order the Guide list uses.
       *
       * Epoch float, same shape as `updatedAt`, so it needs no new formatting. Not
       * sortable: the sort is server-side (see the note on Last promoted below) and
       * `createdAt` is not a key this endpoint is known to accept.
       */
      id: 'createdAt',
      header: 'Created At',
      accessor: (model) => model?.createdAt ?? '',
      width: '160px',
      minWidth: '130px',
      cellType: 'meta',
      render: (_value, model) => (
        <span data-testid={`ui-model-created-${model.id}`}>
          {model?.createdAt
            ? formatDateWithTimeDDMMMYYYY(model.createdAt) ?? EMPTY_CELL
            : EMPTY_CELL}
        </span>
      ),
    },
    {
      id: 'updatedAt',
      header: 'Updated At',
      accessor: (model) => model?.updatedAt ?? model?.createdAt ?? '',
      width: '160px',
      minWidth: '140px',
      sortable: true,
      cellType: 'meta',
      render: (_value, model) =>
        formatDateWithTimeDDMMMYYYY(model?.updatedAt ?? model?.createdAt ?? ''),
    },
    {
      /*
       * The only column here carrying something no other cell implies. The Promoted chip
       * says a model is live and its triangle says there are unpromoted changes — but
       * nothing said how stale the live version is. This does.
       *
       * ⚠ **`last_promoted_at` is ISO 8601; `createdAt`/`updatedAt` are epoch floats.**
       * The shared DD-MMM-YYYY formatter took only the epoch shape and returned a
       * confident `01-Jan-1970` for an ISO string (`parseFloat` stops at the first
       * hyphen). It handles both now — see `Common/HelperClass.ts`.
       */
      id: 'lastPromotedAt',
      header: 'Last Promoted',
      accessor: (model) => model?.last_promoted_at ?? '',
      width: '160px',
      minWidth: '130px',
      cellType: 'meta',
      render: (_value, model) => (
        <span data-testid={`ui-model-last-promoted-${model.id}`}>
          {model?.last_promoted_at
            ? formatDateWithTimeDDMMMYYYY(model.last_promoted_at) ?? EMPTY_CELL
            : EMPTY_CELL}
        </span>
      ),
    },
    {
      id: 'preview',
      header: 'Preview',
      accessor: 'id',
      width: '80px',
      align: 'center',
      render: (_value, model, rowIndex) => (
        <button
          type="button"
          id={`preview-btn-${rowIndex}`}
          data-testid={`ui-model-preview-${model.id}`}
          aria-label={`Preview ${model?.name ?? ''}`}
          onClick={() => {
            let selectedPartyName = (selectedParty!.name.replace(/ /g, '_')).toLowerCase();
            const rootPreviewURL: string = runtimeConfig.ROOT_PREVIEW_URL ?? '';
            const previewUrl: string = `https://${selectedPartyName}.${rootPreviewURL}/preview?modelId=${projectId}&uiModelId=${model.id}&industry=${industryId}${appData.channelName ? `&env=${encodeURIComponent(appData.channelName.toLowerCase())}` : ''}`;
            window.open(previewUrl, '_blank')!.focus();
          }}
        >
          <PreviewIcon />
        </button>
      ),
    },
    {
      // The one column with no width: it collects ALL the table's slack, so
      // the data columns cluster left, the action icons stay pinned right, and
      // the unavoidable void on a wide screen sits in one deliberate place
      // between them instead of stretching Name or gapping every column.
      id: 'spacer',
      header: '',
      accessor: 'id',
      render: () => null,
    },
    {
      id: 'copy',
      header: '',
      accessor: 'id',
      width: '56px',
      align: 'center',
      render: (_value, uiModel, rowIndex) => (
        <button
          type="button"
          id={`copy-btn-${rowIndex}`}
          data-testid={`ui-model-copy-${uiModel.id}`}
          aria-label={`Copy ${uiModel?.name ?? ''}`}
          onClick={() => {
            if (!hasEditPermission()) {
              toast.error('You dont have permission to update this model');
              return;
            }
            setCopyUiModel(uiModel);
          }}
        >
          <CopyIcon />
        </button>
      ),
    },
    {
      id: 'delete',
      header: '',
      accessor: 'id',
      width: '56px',
      align: 'center',
      render: (_value, model, rowIndex) => renderDeleteButton(model, rowIndex),
    },
  ];

  return (
    <>
      <div
        id={`${baseId}-search-wrapper`}
        className="tw-w-[250px] tw-justify-self-end tw-mt-4"
      >
        <Search
          id={`${baseId}-search`}
          data-testid={`${baseId}-search`}
          placeholder="Search..."
          ariaLabel="Search UI Models"
          size="sm"
          value={searchTerm}
          onChange={(value: string) => setSearchTerm(value)}
          /* § 288 — `isLoading` is the same flag the Table below takes as `loading`;
             `searchTerm !== debouncedSearchTerm` covers the 300ms `useDebounce` that
             gates the refetch effect. */
          loading={isLoading || searchTerm !== debouncedSearchTerm}
          loadingLabel="Searching UI models"
        />
      </div>

      <div
        id={`${baseId}-header`}
        data-testid={`${baseId}-header`}
        className="tw-flex tw-justify-between tw-items-center tw-border tw-border-edge tw-px-[22px] tw-py-[8px] tw-mt-[16px]"
      >
        <Typography variant="sub-title-label" as="h2" className="tw-text-ink-secondary" id={`${baseId}-header-title`}>
          UI Models
        </Typography>
        <Button
          variant="tertiary"
          size="sm"
          id={`${baseId}-header-action-btn`}
          data-testid={`${baseId}-header-action-btn`}
          onClick={() => {
            if (!hasEditPermission()) {
              toast.error('You dont have permission to update this model');
              return;
            }
            setShowCreateUiModel(true);
          }}
        >
          Create
        </Button>
      </div>

      <Table<UIModelAPI>
        /* Bound the grid so <main> stops scrolling the page — see
           `pageGridHeight.ts` for the measured reserve. `stickyHeader`
           needs it: sticky resolves against a scrolling ancestor. */
        stickyHeader
        maxHeight={TABBED_GRID_MAX_HEIGHT}
        columns={columns}
        data={models}
        rowKey="id"
        striped
        loading={isLoading}
        sortState={{
          columnId: sort.sortBy,
          direction: sort.sortOrder === 'asc' ? 'asc' : 'desc',
        }}
        onSort={(columnId) => handleSort(columnId)}
        emptyContent={<span className="tw-text-ink-secondary">No data available</span>}
        data-testid="ui-models-table"
      />

      <div
        id={`${baseId}-pagination-wrapper`}
        className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap tw-mt-2"
      >
        <div
          id={`${baseId}-page-size-wrapper`}
          className="tw-flex tw-items-center tw-gap-2"
        >
          <span className="tw-text-ink-secondary">Show</span>
          <Dropdown
            label=""
            aria-label="Records on page select"
            size="sm"
            className="tw-w-[84px]"
            options={PAGE_SIZE_OPTIONS}
            value={paginationParams?.pageSize ?? 10}
            onChange={(value) => updatePageSize(Number(value))}
            data-testid={`${baseId}-page-size-select`}
          />
        </div>
        <Pagination
          page={paginationParams?.pageNumber}
          totalItems={totalRecords}
          pageSize={paginationParams?.pageSize}
          onPageChange={handlePageChange}
          size="sm"
          data-testid={`${baseId}-pagination`}
        />
      </div>

      {showCreateUiModel && (
        <CreateUIModel
          id={`${baseId}-create-form`}
          title="Create UI Model"
          projectId={projectId}
          onSuccess={handleCreateSuccess}
          onClose={() => setShowCreateUiModel(false)}
        />
      )}
      {copyUiModel != null && (
        <CreateUIModel
          id={`${baseId}-copy-form`}
          title="Copy UI Model"
          uiModel={copyUiModel}
          projectId={projectId}
          onSuccess={handleCreateSuccess}
          onClose={() => setCopyUiModel(null)}
          isCopyModel={true}
        />
      )}
      <DeleteProjectModal
        showModal={deleteModal != null}
        closeModal={() => setDeleteModal(null)}
        loading={deleteLoading}
        onConfirm={async () => {
          setDeleteLoading(true);
          try {
            const result = await deleteUIModel(deleteModal?.id ?? '');
            if (result?.done) {
              await fetchModels();
              toast.success('UI Model deleted successfully');
            } else {
              toast.error(result?.message ?? 'Failed to delete UI model');
            }
          } catch (e: any) {
            toast.error(e.message ?? 'Failed to delete UI model');
          }
          setDeleteLoading(false);
          setDeleteModal(null);
        }}
        deleteItem={deleteModal}
      />

    </>
  );
};

export default UIModelTable;
